import {useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";
import dayjs from "dayjs";
import './AssetHistoryCalendar.scss'
import {getHistoryArray, getIsCalendarPercent} from "../../../ducks/history-duck.js";
import {
    getAssetIds,
    getCurrentAsset,
    getFilterPreset,
    getIsSingleAsset,
    setDateFilter
} from "../../../ducks/assets-duck.js";
import {getIsOddy} from "../../../ducks/user-duck.js";
import {activateWidePopup} from "../../../ducks/popup-duck.js";
import {getDirectionColorClassName} from "../../../util/misc.js";
import {DECIMAL_FORMAT, numberFormat} from "../../../util/numbers.js";
import {isToday} from "../../../util/time-and-date.js";
import {scrollToTop, useNavigation} from "../../util/navigation/navigation.js";
import AddRateDialog from "./AddRateDialog.jsx";
import StarIcon from "mdi-react/StarIcon";
import SkullIcon from "mdi-react/SkullIcon";

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// Mirrors $positive / $negative in modules/util/scss/colors.scss and the
// neutral '.day' background-color below - keep in sync if those change.
const NEUTRAL_RGBA = [0, 0, 0, 0.25]
const POSITIVE_RGBA = [96, 236, 124, 0.16]
const NEGATIVE_RGBA = [148, 105, 192, 0.22]

const mixRgba = (from, to, ratio) => {
    const clamped = Math.min(Math.max(ratio, 0), 1)
    const [r, g, b, a] = from.map((value, idx) => value + (to[idx] - value) * clamped)
    return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a.toFixed(3)})`
}

const getDayTotals = (data, assetIds) => assetIds.reduce((acc, cur) => ({
    value: acc.value + data[cur].value,
    valueYesterday: acc.valueYesterday + data[cur].valueYesterday,
    gain: acc.gain + data[cur].gain,
    gainChange: acc.gainChange + (data[cur].value - (data[cur].value / (1 + data[cur].gainChangePercent))),
}), {value: 0, valueYesterday: 0, gain: 0, gainChange: 0})

export default () => {

    const dispatch = useDispatch()
    const history = useSelector(getHistoryArray)
    const filterPreset = useSelector(getFilterPreset)
    const assetIds = useSelector(getAssetIds)
    const isSingleAsset = useSelector(getIsSingleAsset)
    const isOddy = useSelector(getIsOddy)
    const {id: currentAssetId, name: currentAssetName, currentRate} = useSelector(getCurrentAsset)

    const {navigateToDashBoard} = useNavigation(scrollToTop)

    const historyMap = useMemo(() => Object.fromEntries(history), [history])

    const selectDay = date => {
        if (isSingleAsset && isOddy) {
            dispatch(activateWidePopup(
                <AddRateDialog
                    date={date}
                    currentRate={historyMap[date]?.[currentAssetId]?.rate || currentRate}
                    assetId={currentAssetId}
                    assetName={currentAssetName}
                />
            ))
            return
        }
        dispatch(setDateFilter(isToday(date) ? null : date))
        navigateToDashBoard(filterPreset)
    }

    const year = dayjs().year()
    const currentMonth = dayjs().month()
    const months = Array.from({length: currentMonth + 1}, (_, idx) => currentMonth - idx)

    return (
        <div className='asset-history-calendar'>
            {months.map(month =>
                <MonthGrid
                    key={month}
                    year={year}
                    month={month}
                    historyMap={historyMap}
                    assetIds={assetIds}
                    onSelectDay={selectDay}
                />)
            }
        </div>
    )
}

const MonthGrid = ({year, month, historyMap, assetIds, onSelectDay}) => {

    const firstOfMonth = dayjs(new Date(year, month, 1))
    const daysInMonth = firstOfMonth.daysInMonth()
    const leadingBlanks = (firstOfMonth.day() + 6) % 7 // Mon = 0 ... Sun = 6

    const today = dayjs()
    let lastVisibleDay = daysInMonth
    if (today.year() === year && today.month() === month) {
        const daysUntilEndOfWeek = 6 - ((today.day() + 6) % 7)
        const endOfCurrentWeek = today.add(daysUntilEndOfWeek, 'day')
        lastVisibleDay = endOfCurrentWeek.month() === month ?
            Math.min(daysInMonth, endOfCurrentWeek.date()) :
            daysInMonth
    }

    const cells = [
        ...Array.from({length: leadingBlanks}, () => null),
        ...Array.from({length: lastVisibleDay}, (_, idx) => idx + 1),
    ]
    while (cells.length % 7 !== 0) {
        cells.push(null)
    }
    const weeks = []
    for (let i = 0; i < cells.length; i += 7) {
        weeks.push(cells.slice(i, i + 7))
    }
    weeks.reverse()

    const monthDays = Array.from({length: lastVisibleDay}, (_, idx) => idx + 1)
        .map(day => historyMap[`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`])
        .filter(Boolean)

    const monthTotals = monthDays.length > 0 ?
        monthDays.reduce((acc, data) => {
            const {valueYesterday, gainChange} = getDayTotals(data, assetIds)
            return {
                gainChange: acc.gainChange + gainChange,
                valueYesterday: acc.valueYesterday ?? valueYesterday,
            }
        }, {gainChange: 0, valueYesterday: null}) :
        null

    const monthChangePercent = monthTotals ? monthTotals.gainChange / monthTotals.valueYesterday : null

    return (
        <div className='month-grid'>
            <div className='month-header'>
                {firstOfMonth.format('MMMM YYYY')}
                {monthTotals && monthTotals.gainChange !== 0 &&
                <span className={`month-change ${getDirectionColorClassName(monthChangePercent, true)}`}>
                    {monthTotals.gainChange > 0 ? '+' : '-'}
                    {numberFormat(Math.abs(monthTotals.gainChange), DECIMAL_FORMAT.FLEX_INT)}
                    {' '}
                    <span className='relative-change'>
                        {numberFormat(Math.abs(monthChangePercent * 100), DECIMAL_FORMAT.FLEX_PERCENTAGE)}%
                    </span>
                </span>
                }
            </div>
            <div className='weekdays'>
                {WEEKDAY_LABELS.map(label =>
                    <div key={label} className='weekday'>
                        {label}
                    </div>)
                }
            </div>
            <div className='weeks'>
                {weeks.map((week, weekIdx) =>
                    <div key={weekIdx} className='week'>
                        {week.map((day, dayIdx) =>
                            day === null ?
                                <div key={dayIdx} className='day is-blank'/>
                                :
                                <DayCell
                                    key={dayIdx}
                                    date={`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`}
                                    day={day}
                                    historyMap={historyMap}
                                    assetIds={assetIds}
                                    onSelectDay={onSelectDay}
                                />)
                        }
                    </div>)
                }
            </div>
        </div>
    )
}

const DayCell = ({date, day, historyMap, assetIds, onSelectDay}) => {

    const data = historyMap[date]
    const isPercent = useSelector(getIsCalendarPercent)

    if (!data) {
        return (
            <div className='day is-empty'>
                <div className='day-number'>
                    {day}
                </div>
            </div>
        )
    }

    const {value, valueYesterday, gain, gainChange} = getDayTotals(data, assetIds)

    const gainChangePercent = gainChange / valueYesterday
    const isFlat = gainChange === 0
    const changePercentPoints = gainChangePercent * 100

    const perAssetChanges = assetIds.map(cur => data[cur].value - (data[cur].value / (1 + data[cur].gainChangePercent)))
    const allAssetsPositive = perAssetChanges.length > 1 && perAssetChanges.every(change => change > 0)
    const allAssetsNegative = perAssetChanges.length > 1 && perAssetChanges.every(change => change < 0)

    let dayStyle
    let extremeClassName = ''
    if (changePercentPoints >= 5) {
        extremeClassName = 'is-extreme-up'
    } else if (changePercentPoints > 0) {
        dayStyle = {backgroundColor: mixRgba(NEUTRAL_RGBA, POSITIVE_RGBA, changePercentPoints / 4)}
    } else if (changePercentPoints <= -5) {
        extremeClassName = 'is-extreme-down'
    } else if (changePercentPoints < 0) {
        dayStyle = {backgroundColor: mixRgba(NEUTRAL_RGBA, NEGATIVE_RGBA, -changePercentPoints / 4)}
    }

    return (
        <div
            className={`day ${extremeClassName} ${isToday(date) ? 'is-today' : ''}`}
            style={dayStyle}
            onClick={() => onSelectDay(date)}
            title={`Value: kr ${numberFormat(value, DECIMAL_FORMAT.FLEX_INT)}, Return: kr ${numberFormat(gain, DECIMAL_FORMAT.FLEX_INT)}`}
        >
            <div className='day-number'>
                {day}
            </div>
            {allAssetsPositive && <StarIcon className='day-badge is-star'/>}
            {allAssetsNegative && <SkullIcon className='day-badge is-skull'/>}
            {!isFlat &&
                <>
                    <div className={`day-amount hide-portrait ${getDirectionColorClassName(gainChangePercent, true)}`}>
                        {gainChange > 0 ? '+' : '-'}{numberFormat(Math.abs(gainChange), 0)}
                    </div>
                    <div className={`day-change ${!isPercent ? 'hide-portrait' : ''} ${getDirectionColorClassName(gainChangePercent, true)}`}>
                        {numberFormat(Math.abs(gainChangePercent * 100), DECIMAL_FORMAT.FLEX_PERCENTAGE)}%
                    </div>
                    <div className={`day-amount-mobile hide-desktop ${isPercent ? 'hide-portrait' : ''} ${getDirectionColorClassName(gainChangePercent, true)}`}>
                        {numberFormat(Math.abs(gainChange), Math.abs(gainChange) >= 1_000_000 ? DECIMAL_FORMAT.FLEX : 0)}
                    </div>
                </>
            }
        </div>
    )
}
